import React, { ReactNode, useEffect, useState } from 'react'
import styled from 'styled-components/macro'
import { Range } from 'react-date-range'
import { EditableField } from 'src/components/Reusable/Forms/EditableField'
import { StyledTextInput } from 'src/components/Reusable/Inputs/TextInput/styles'
import { TrusteroDateRange } from 'src/components/Reusable/TrusteroDateRange'
import { formatISODate } from 'src/Utils/formatDate'
import { MdEditor, Markup } from 'src/components/Reusable/Text/Markup'
import { ShowPageOwnerDropdown } from 'src/components/Reusable/ShowPageOwnerDropdown'
import { ShowPageAssigneesField } from 'src/components/Reusable/ShowPageAssigneesField'
import { useAssigneeDisplayNames } from 'src/components/Reusable/AssigneeDisplay'
import { Tooltip } from 'src/components/Reusable/Tooltip'
import {
  ShowPageDetailSection,
  ShowPageDetailLabel,
  ShowPageDetailValue,
} from './BaseComponents'

// Shared "detail field" wrappers for show pages: each renders a
// ShowPageDetailSection + label + inline-editable value (EditableField), owning
// its own edit/pending state so the caller only supplies the value + an
// onSave callback.

const DetailLabel = ({
  label,
  tooltip,
}: {
  label: string
  tooltip?: ReactNode
}): JSX.Element =>
  tooltip ? (
    <ShowPageDetailLabel
      style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
    >
      {label}
      {tooltip}
    </ShowPageDetailLabel>
  ) : (
    <ShowPageDetailLabel>{label}</ShowPageDetailLabel>
  )

// Same editor styling as the Request show page's markdown Description.
const MdEditorWrapper = styled.div`
  width: 100%;
  .react-mde {
    width: 100%;
    border-radius: 8px;
    overflow: hidden;
  }
  .mde-header {
    border-radius: 8px 8px 0 0;
  }
  .mde-text {
    border-radius: 0 0 8px 8px;
  }
`

// A text value that hover-reveals a pencil and edits inline, for rows that
// bring their own label. `markdown` swaps in a markdown editor and renders the
// saved value as markup. An onSave resolving false keeps the editor open.
export const EditableTextValue = ({
  value,
  placeholder,
  ariaLabel,
  maxLength,
  disabled = false,
  markdown = false,
  isIconAtEnd = false,
  onSave,
}: {
  value: string
  placeholder: string
  ariaLabel?: string
  maxLength?: number
  disabled?: boolean
  markdown?: boolean
  // See EditableField.
  isIconAtEnd?: boolean
  onSave: (value: string) => Promise<boolean | void>
}): JSX.Element => {
  const [draft, setDraft] = useState(value)
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    setDraft(value)
  }, [value])

  return (
    <EditableField
      fullWidth
      disabled={disabled}
      isSaving={isSaving}
      isIconAtEnd={isIconAtEnd}
      renderView={() =>
        value ? (
          markdown ? (
            <Markup markdown={value} sectionProps={{ mb: 0 }} />
          ) : (
            <ShowPageDetailValue>{value}</ShowPageDetailValue>
          )
        ) : (
          <ShowPageDetailValue isDisabled>{placeholder}</ShowPageDetailValue>
        )
      }
      isEditing={isEditing}
      onEdit={() => {
        setDraft(value)
        setIsEditing(true)
      }}
      onSave={async () => {
        setIsSaving(true)
        try {
          if ((await onSave(draft)) !== false) setIsEditing(false)
        } finally {
          setIsSaving(false)
        }
      }}
      onCancel={() => {
        setDraft(value)
        setIsEditing(false)
      }}
    >
      {markdown ? (
        <MdEditorWrapper>
          <MdEditor value={draft} onChange={setDraft} />
        </MdEditorWrapper>
      ) : (
        <StyledTextInput
          aria-label={ariaLabel}
          maxLength={maxLength}
          value={draft}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setDraft(e.target.value)
          }
          showError={false}
        />
      )}
    </EditableField>
  )
}

const dateRange = (ms: number): Range => {
  const date = ms > 0 ? new Date(ms) : undefined
  return { startDate: date, endDate: date }
}

// A date value edited with a single-date picker; clearing it saves 0. An
// onSave resolving false keeps the picker open.
export const EditableDateValue = ({
  valueMs,
  disabled = false,
  isIconAtEnd = false,
  onSave,
}: {
  valueMs: number
  disabled?: boolean
  // See EditableField.
  isIconAtEnd?: boolean
  onSave: (valueMs: number) => Promise<boolean | void>
}): JSX.Element => {
  const [range, setRange] = useState<Range>(dateRange(valueMs))
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => setRange(dateRange(valueMs)), [valueMs])

  return (
    <EditableField
      disabled={disabled}
      isSaving={isSaving}
      isIconAtEnd={isIconAtEnd}
      renderView={() => (
        <ShowPageDetailValue isDisabled={!valueMs}>
          {valueMs > 0 ? formatISODate(new Date(valueMs)) : '—'}
        </ShowPageDetailValue>
      )}
      isEditing={isEditing}
      onEdit={() => setIsEditing(true)}
      onSave={async () => {
        setIsSaving(true)
        try {
          const next = range.endDate ? range.endDate.getTime() : 0
          if ((await onSave(next)) !== false) setIsEditing(false)
        } finally {
          setIsSaving(false)
        }
      }}
      onCancel={() => {
        setRange(dateRange(valueMs))
        setIsEditing(false)
      }}
    >
      <TrusteroDateRange
        single
        range={range}
        rangeSet={setRange}
        width="200px"
      />
    </EditableField>
  )
}

// A single-line text field that hover-reveals a pencil and edits inline.
// `markdown` swaps in a markdown editor and renders the saved value as markup.
export const EditableTextDetailField = ({
  label,
  tooltip,
  ...valueProps
}: {
  label: string
  tooltip?: ReactNode
  value: string
  placeholder: string
  disabled?: boolean
  markdown?: boolean
  onSave: (value: string) => Promise<void>
}): JSX.Element => (
  <ShowPageDetailSection hasEditableValue>
    <DetailLabel label={label} tooltip={tooltip} />
    <EditableTextValue {...valueProps} />
  </ShowPageDetailSection>
)

// Owner field: view shows the resolved display name (or "Unassigned"); edit uses
// the shared owner dropdown. onSave receives the email ('' when unassigned).
export const EditableOwnerField = ({
  label = 'Owner',
  ownerEmail,
  disabled = false,
  onSave,
}: {
  label?: string
  ownerEmail: string
  disabled?: boolean
  onSave: (email: string) => Promise<void>
}): JSX.Element => {
  const { displayName } = useAssigneeDisplayNames()
  const [isEditing, setIsEditing] = useState(false)
  const [pending, setPending] = useState<string | null>(null)

  const handleSave = async () => {
    if (pending !== null) {
      await onSave(pending === 'Unassigned' ? '' : pending)
    }
    setIsEditing(false)
  }

  return (
    <ShowPageDetailSection>
      <ShowPageDetailLabel>{label}</ShowPageDetailLabel>
      <EditableField
        disabled={disabled}
        renderView={() => (
          <ShowPageDetailValue isDisabled={!ownerEmail}>
            {ownerEmail ? displayName(ownerEmail) : 'Unassigned'}
          </ShowPageDetailValue>
        )}
        isEditing={isEditing}
        onEdit={() => {
          setPending(ownerEmail)
          setIsEditing(true)
        }}
        onSave={handleSave}
        onCancel={() => {
          setPending(null)
          setIsEditing(false)
        }}
      >
        <ShowPageOwnerDropdown
          ownerEmail={pending ?? ownerEmail}
          hideLabel
          onSave={async (email) => setPending(email)}
        />
      </EditableField>
    </ShowPageDetailSection>
  )
}

// Assignees field: view shows the first assignee + "+ N more" (all names in a
// tooltip); edit uses the shared multi-select assignees field.
export const EditableAssigneesField = ({
  label = 'Assignees',
  modelId,
  assigneeEmails,
  disabled = false,
  onSave,
}: {
  label?: string
  modelId: string
  assigneeEmails: string[]
  disabled?: boolean
  onSave: (emails: string[]) => Promise<void>
}): JSX.Element => {
  const { displayName } = useAssigneeDisplayNames()
  const [isEditing, setIsEditing] = useState(false)
  const [pending, setPending] = useState<string[] | null>(null)

  const handleSave = async () => {
    if (pending !== null) {
      await onSave(pending)
    }
    setIsEditing(false)
  }

  return (
    <ShowPageDetailSection>
      <ShowPageDetailLabel>{label}</ShowPageDetailLabel>
      <EditableField
        disabled={disabled}
        renderView={() =>
          assigneeEmails.length > 0 ? (
            <Tooltip
              id={`assignees-tooltip-${modelId}`}
              placement="top"
              tooltipBody={assigneeEmails.map(displayName).join(', ')}
            >
              <ShowPageDetailValue>
                {displayName(assigneeEmails[0])}
                {assigneeEmails.length > 1 &&
                  ` + ${assigneeEmails.length - 1} more`}
              </ShowPageDetailValue>
            </Tooltip>
          ) : (
            <ShowPageDetailValue isDisabled>Unassigned</ShowPageDetailValue>
          )
        }
        isEditing={isEditing}
        onEdit={() => {
          setPending(assigneeEmails)
          setIsEditing(true)
        }}
        onSave={handleSave}
        onCancel={() => {
          setPending(null)
          setIsEditing(false)
        }}
      >
        <ShowPageAssigneesField
          modelId={modelId}
          assigneeEmails={pending ?? assigneeEmails}
          hideLabel
          onSave={async (emails) => setPending(emails)}
        />
      </EditableField>
    </ShowPageDetailSection>
  )
}
